import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  httpGet,
  httpPost,
} from "../../services/httpService";
import {
  getTotalRevenueTrendUrl,
  getAverageComplianceUrl,
  getServiesPerformanceUrl,
  getTotalBookingsGrowthUrl,
  getAnalyticsReportsUrl,
  getPerformanceDistributionUrl,
  getAnalyticsPerformanceUrl,
  getAllAnalyticsReports,
  getAnalyticsReportByIdUrl,
  getServiceDetailsUrl,
  getMemberDetailsUrl,
} from "../../apiUrls/apiUrls";

const initialState = {
  // Services Tab
  revenueTrend: [],
  averageCompliance: [],
  servicesPerformance: [],
  bookingsGrowth: [],
  // Members Tab
  memberPerformance: [],
  membersCompliance: [],
  performanceDistribution: [],
  memberDetails: [],
  isMemberDetailsLoading: false,
  // Reports & Global
  analyticsReports: [],
  allAnalyticsReports: [],
  selectedReportData: [], // Add this for individual report data
  isAnalyticsLoading: false,
  error: null,
  serviceDetails: [],
  isServiceDetailsLoading: false,
};

export const getTotalRevenueTrendData = createAsyncThunk(
  "analytics/getTotalRevenueTrendData",
  async (params, thunkApi) => {
    const urlToGet = getTotalRevenueTrendUrl();
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const getAnalyticsReportsData = createAsyncThunk(
  "analytics/getAnalyticsReportsData",
  async (params, thunkApi) => {
    const urlToGet = getAnalyticsReportsUrl();
    const respData = await httpGet(urlToGet);
    return respData;
  },
);

export const getServicesTabData = createAsyncThunk(
  "analytics/getServicesTabData",
  async (params, { rejectWithValue }) => {
    try {
      const results = await Promise.allSettled([
        httpPost(getTotalRevenueTrendUrl(), params),
        httpPost(getAverageComplianceUrl(), params),
        httpPost(getServiesPerformanceUrl(), params),
        httpPost(getTotalBookingsGrowthUrl(), params),
      ]);

      const getData = (res) => (res.status === "fulfilled" ? res.value : null);

      const rev = getData(results[0]);
      const comp = getData(results[1]);
      const perf = getData(results[2]);
      const book = getData(results[3]);
      return {
        revenue: rev?.response || rev || [],
        compliance: comp?.response || comp || [],
        servicePerformance: perf?.response || perf || [],
        bookings: book?.response || book || [],
      };
    } catch (error) {
      return rejectWithValue("Unexpected error occurred");
    }
  },
);

export const getMembersTabData = createAsyncThunk(
  "analytics/getMembersTabData",
  async (params, { rejectWithValue }) => {

    try {
      const results = await Promise.allSettled([
        httpPost(getAnalyticsPerformanceUrl(), params),
        httpPost(getServiesPerformanceUrl(), params),
        httpPost(getPerformanceDistributionUrl(),params),
      ]);

      const getData = (res) => (res.status === "fulfilled" ? res.value : null);

      const performance = getData(results[0]);
      const compliance = getData(results[1]);
      const distribution = getData(results[2]);

      return {
        memberPerformance: performance?.response || performance || [],
        membersCompliance: compliance?.response || compliance || [],
        performanceDistribution: distribution?.response || distribution || [],
      };
    } catch (error) {
      console.error("Members Thunk Error:", error);
      return rejectWithValue(
        error.message || "Failed to fetch members analytics",
      );
    }
  },
);

export const fetchAllAnalyticsReportsData = createAsyncThunk(
  "analytics/fetchAllAnalyticsReportsData",
  async (params, { rejectWithValue }) => {
    const { companyID, pharmacyID } = params;
    try {
      const url = getAllAnalyticsReports();
      const respData = await httpGet(url);

      return respData?.response || respData || [];
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch all reports");
    }
  },
);

// Add this new thunk for fetching individual report data
export const fetchAnalyticsReportById = createAsyncThunk(
  "analytics/fetchAnalyticsReportById",
  async (params, { rejectWithValue }) => {
    const { reportID, startDate, endDate, companyID, pharmacyID } = params;
    try {
      const url = getAnalyticsReportByIdUrl(
        reportID,
        startDate,
        endDate,
        companyID,
        pharmacyID,
      );
      const respData = await httpGet(url);

      return respData?.response || respData || [];
    } catch (error) {
      return rejectWithValue(error.message || "Failed to fetch report data");
    }
  },
);

export const getServiceDetailsData = createAsyncThunk(
  "analytics/getServiceDetailsData",
  async (params, thunkApi) => {
    const url = getServiceDetailsUrl();
    const respData = await httpPost(url, params);
    return respData;
  },
);

export const getMemberDetailsData = createAsyncThunk(
  "analytics/getMemberDetailsData",
  async (params, thunkApi) => {
    const url = getMemberDetailsUrl();
    const respData = await httpPost(url, params);
    return respData;
  },
);

export const analytics = createSlice({
  name: "analytics",
  initialState,
  reducers: {
    // Add a reducer to clear selected report data
    clearSelectedReportData: (state) => {
      state.selectedReportData = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getServicesTabData.pending, (state) => {
        state.isAnalyticsLoading = true;
        state.error = null;
      })
      .addCase(getServicesTabData.fulfilled, (state, action) => {
        state.revenueTrend = action.payload.revenue;
        state.averageCompliance = action.payload.compliance;
        state.servicesPerformance = action.payload.servicePerformance;
        state.bookingsGrowth = action.payload.bookings;
        state.isAnalyticsLoading = false;
      })
      .addCase(getServicesTabData.rejected, (state, action) => {
        state.isAnalyticsLoading = false;
        state.error = action.error.message;
      })
      .addCase(getAnalyticsReportsData.pending, (state, action) => {
        state.isAnalyticsLoading = true;
      })
      .addCase(getAnalyticsReportsData.fulfilled, (state, action) => {
        state.analyticsReports = action?.payload;
        state.isAnalyticsLoading = false;
      })
      .addCase(getAnalyticsReportsData.rejected, (state, action) => {
        state.isAnalyticsLoading = false;
      })
      .addCase(getMembersTabData.pending, (state) => {
        state.isAnalyticsLoading = true;
        state.error = null;
      })
      .addCase(getMembersTabData.fulfilled, (state, action) => {
        state.memberPerformance = action.payload.memberPerformance;
        state.membersCompliance = action.payload.membersCompliance;
        state.performanceDistribution = action.payload.performanceDistribution;
        state.isAnalyticsLoading = false;
      })
      .addCase(getMembersTabData.rejected, (state, action) => {
        state.isAnalyticsLoading = false;
        state.error = action.error.message;
      })
      .addCase(fetchAllAnalyticsReportsData.pending, (state) => {
        state.isAnalyticsLoading = true;
        state.error = null;
      })
      .addCase(fetchAllAnalyticsReportsData.fulfilled, (state, action) => {
        state.allAnalyticsReports = action.payload;
        state.isAnalyticsLoading = false;
      })
      .addCase(fetchAllAnalyticsReportsData.rejected, (state, action) => {
        state.isAnalyticsLoading = false;
        state.error = action.payload;
      })
      // Add cases for the new report fetch thunk
      .addCase(fetchAnalyticsReportById.pending, (state) => {
        state.isAnalyticsLoading = true;
        state.error = null;
      })
      .addCase(fetchAnalyticsReportById.fulfilled, (state, action) => {
        state.selectedReportData = action.payload;
        state.isAnalyticsLoading = false;
      })
      .addCase(fetchAnalyticsReportById.rejected, (state, action) => {
        state.isAnalyticsLoading = false;
        state.error = action.payload;
      })
      .addCase(getServiceDetailsData.pending, (state) => {
        state.isServiceDetailsLoading = true;
      })
      .addCase(getServiceDetailsData.fulfilled, (state, action) => {
        state.serviceDetails = action.payload;
        state.isServiceDetailsLoading = false;
      })
      .addCase(getServiceDetailsData.rejected, (state, action) => {
        state.isServiceDetailsLoading = false;
        state.serviceDetails = [];
      })
      .addCase(getMemberDetailsData.pending, (state) => {
        state.isMemberDetailsLoading = true;
      })
      .addCase(getMemberDetailsData.fulfilled, (state, action) => {
        state.memberDetails = action.payload;
        state.isMemberDetailsLoading = false;
      })
      .addCase(getMemberDetailsData.rejected, (state, action) => {
        state.isMemberDetailsLoading = false;
        state.memberDetails = [];
      });
  },
});

export const { clearSelectedReportData } = analytics.actions;

export default analytics.reducer;
